import Cart from '$lib/utils/shopify/cart';
import gql from 'graphql-tag';
import { client } from '$lib/utils/shopify/publicStorefrontClient';
import { publish } from '$lib/utils/pubsub';
import { cartUpdate } from '$lib/utils/shopify/cart/events';
import { fragment as cartDataFragment } from '$lib/utils/shopify/fragments/cartFragment';
import { getCustomerAccessToken } from '../customer/getCustomerAccessToken';
import device from '$lib/utils/functions/device';

// pubsub cart dispatcher
const dispatchChanges = (message) => {
    publish(new cartUpdate(message));
};

/**
 * @description create shopify cart
 * @returns {Promise} - Returns a new cart
 */
async function createCart() {
    //GQL
    const createCartMutation = gql`
        mutation cartCreate {
            cartCreate {
                cart {
                    ...cartData
                }
            }
        }
        ${cartDataFragment}
    `;

    return client()
        .mutate({
            mutation: createCartMutation,
            variables: {}
        })
        .then((response) => response?.data?.cartCreate?.cart)
        .catch((e) => {
            throw e;
        });
}

/**
 * get existing shopify cart
 * @param {string} cartId - Shopify Cart GID.
 * @returns {Promise} - a cart object if it exists, or null (maybe) if not
 */
async function getCart(cartId) {
    const getCartQuery = gql`
        query getCartByID($cartId: ID!) {
            cart(id: $cartId) {
                ...cartData
            }
        }
        ${cartDataFragment}
    `;
    return client()
        .query({
            query: getCartQuery,
            variables: { cartId }
        })
        .then((response) => response?.data?.cart)
        .catch((e) => {
            throw e;
        });
}

/**
 * @description add customer to cart
 * @param {string} cartId - the cart GID to add customer to
 * @param {string} customerAccessToken - the storefront access token for this customer
 * @returns {Promise} Cart - If customer succesfully assigned, null if not
 */
async function updateBuyerIdentity(cartId, customerAccessToken) {
    // GQL
    const buyerIdentity = gql`
        mutation cartBuyerIdentityUpdate($buyerIdentity: CartBuyerIdentityInput!, $cartId: ID!) {
            cartBuyerIdentityUpdate(buyerIdentity: $buyerIdentity, cartId: $cartId) {
                cart {
                    ...cartData
                }
                userErrors {
                    code
                    field
                    message
                }
            }
        }
        ${cartDataFragment}
    `;

    return client()
        .mutate({
            mutation: buyerIdentity,
            variables: {
                cartId,
                buyerIdentity: {
                    customerAccessToken
                }
            }
        })
        .then((response) => response?.data?.cartBuyerIdentityUpdate?.cart)
        .catch((e) => {
            throw e;
        });
}

// init cart
export async function initializeCart({ persistent = false }) {
    let cart;
    let customerToken;

    let storedCartID = window.localStorage.getItem('shopify:cart'); // TODO : Move to a cookie with < 10 day window? For now we will keep this persistent in localstorage.
    if (persistent) customerToken = await getCustomerAccessToken(); // Storefront Token or null

    if (storedCartID) {
        cart = await getCart(storedCartID).then((cart) => (cart ? cart : createCart())); // Get or otherwise create cart
    } else {
        cart = await createCart(); // Create cart
    }
    if (customerToken && customerToken !== 'null') {
        // Add a buyer identity if customer has accessToken, otherwise return unassociated cart
        const customerAssociatedCart = await updateBuyerIdentity(cart.id, customerToken);
        if (customerAssociatedCart) cart = customerAssociatedCart;
    }
    // Set and dispatch all changes
    window.localStorage.setItem('shopify:cart', cart.id); // Used here for initalization // TODO : Move to a cookie with < 10 day window? For now we will keep this persistent in localstorage.
    window.cart = new Cart(cart); // Used by CartItem, ProductBuy to add or mutate products
    dispatchChanges({ cart }); // Push the cart data after the chain above to the cart listeners

    if (device.isPWA(window)) {
        const deviceType = device.isMobile() || 'Unknown Device';
        window.cart.addCartAttributes([
            { key: 'PWA Order', value: 'true' },
            { key: 'Device', value: deviceType }
        ]);
    }
}
